<template>
  <FreeBottomSheet
    :name="name"
    persistent
    @onTryClose="onClose"
    @onOpen="syncDraftPassenger"
  >
    <template #header>
      <FreeHeader class="p-4">
        <template #default>
          <h3 class="text-text-neutral-primary text-base font-medium">
            {{ $t('passengersData.multiPassengers.title') }}
          </h3>
        </template>
        <template #rightAction>
          <FreeCloseButton @click="onClose" />
        </template>
      </FreeHeader>
    </template>
    <template #default>
      <div class="space-y-6 p-4">
        <PassengerFormContainer
          :is-domestic="isDomestic"
          :passenger="draftPassenger"
          :passenger-key="passengerKey"
          :validations="validations"
          :provider-class="providerClass"
          :airline-bonus-cards-schema="airlineBonusCardsSchema"
          :is-submit-attempted="isSubmitAttempted"
          @onPassengerUpdate="onPassengerUpdate"
          @onMilesCardStateUpdate="onMilesCardStateUpdate"
        />
        <FreeButton
          type="button"
          class="green-main-button w-full"
          @click="onSubmit"
        >
          {{ $t('general.save') }}
        </FreeButton>
      </div>
      <ConfirmationModal
        :modal-name="unsavedModalName"
        class="top-auto h-screen"
        @onAgree="onBottomSheetCloseAgree"
      />
      <FreeAlert
        ref="duplicatePaxAlertRef"
        class="fixed bottom-20 mx-4 flex grow"
        :duration="3000"
      >
        <template #icon>
          <img
            src="@/assets/images/red-warning.svg"
            width="24"
            height="24"
            alt=""
          />
        </template>
        <template #default>
          <p class="text-text-neutral-primary">
            {{ $t('passengersData.duplicatePaxError') }}
          </p>
        </template>
      </FreeAlert>
    </template>
  </FreeBottomSheet>
</template>

<script setup lang="ts">
  import { ref, computed, watch } from 'vue';
  import { useI18n } from 'vue-i18n';
  import { useVuelidate } from '@vuelidate/core';
  import {
    FreeHeader,
    FreeButton,
    FreeBottomSheet,
    FreeCloseButton,
    ConfirmationModal,
    FreeAlert,
  } from 'freedom-shared';
  import { v4 as uuid } from 'uuid';
  import { clone, isEqual } from 'lodash-es';
  import { passengerValidation, controllerModel } from 'shared';
  import type { Airflow } from '@aviata/platform-types';
  import type { PassengerKey, Passenger } from '@aviata/booking-data';

  import { savedPassengersConfig } from '@/features/saved-passengers';
  import { bookingStateModel } from '@/features/booking-state';

  import {
    formPassengerConfig,
    formPassengerModel,
  } from '@/entities/form-passenger';

  import { sentryModel, useUserStore } from '@/shared/model';
  import { analyticsEventsLib } from '@/shared/lib';

  import { convertPassengerToPayload, milesCardsStorageLib } from '../lib';
  import PassengerFormContainer from './PassengerFormContainer.vue';

  const props = defineProps<{
    name: string;
    savedPassengersBottomSheetName: string;
    allPassengers: Passenger[];
    passengerOnCard: Passenger | null;
    passengerKey: PassengerKey;
    passengerIdx: number | null;
    isDomestic: boolean;
    schema: Airflow.Offer.Schema.PassengerEntity;
    providerClass?: string;
    isNewPassenger?: boolean;
  }>();

  const emit = defineEmits<{
    onSubmit: [];
    onPassengerEdit: [updatedPassenger: Passenger, idx: number];
    onPassengerCreate: [passenger: Passenger];
  }>();

  const { t } = useI18n();
  const bottomSheet = controllerModel.useBottomSheet();
  const modal = controllerModel.useModal();
  const unsavedModalName = `unsaved-data-modal-${uuid()}`;

  const bookingStore = bookingStateModel.useBookingStore();

  const airlineBonusCardsSchema = computed(
    () => props.schema.schema.airline_bonus_cards?.schema,
  );

  const selectedPassenger = computed<Passenger | null>(() =>
    props.passengerIdx !== null
      ? props.allPassengers[props.passengerIdx]
      : (props.passengerOnCard ?? formPassengerConfig.emptyPassenger),
  );
  const draftPassenger = ref(props.passengerOnCard);
  const isSubmitAttempted = ref(false);
  const milesCardState = ref({
    hasMilesCard: false,
    milesCardNumber: '',
  });
  // Храним исходное значение пассажира при открытии формы для корректной проверки изменений
  const initialPassengerOnCard = ref<Passenger | null>(
    props.passengerOnCard ? clone(props.passengerOnCard) : null,
  );

  const duplicatePaxAlertRef = ref();
  const handleDuplicatePaxError = () => {
    duplicatePaxAlertRef.value?.show();
  };

  const validationRules = passengerValidation.lib.initValidation({
    schema: props.schema,
    passengerKey: props.passengerKey,
    validationMessagePath: 'passengersData.validations',
    citizenship: computed(() => draftPassenger.value?.citizenship),
    departureDate: computed(
      () => bookingStore.mappedFlights[0].segments[0].dep.at,
    ),
    providerClass: props.providerClass,
  });

  const validations = useVuelidate(
    validationRules,
    computed(() => draftPassenger.value),
  );

  const onPassengerUpdate = (updatedPassenger: Passenger) =>
    (draftPassenger.value = updatedPassenger);

  const onMilesCardStateUpdate = (payload: {
    hasMilesCard: boolean;
    milesCardNumber: string;
  }) => {
    // Черновое состояние карты обновляется только в рамках формы.
    // В localStorage пишем только на подтвержденном сохранении.
    milesCardState.value = payload;
  };

  const syncDraftPassenger = () => {
    draftPassenger.value = clone(selectedPassenger.value);
    isSubmitAttempted.value = false;
    // При открытии формы показываем сохраненное значение из localStorage,
    // чтобы UI всегда отражал последнее подтвержденное состояние.
    const milesCardNumber =
      milesCardsStorageLib.getMilesCardNumberByPassengerId(
        selectedPassenger.value?.id ?? '',
      );

    milesCardState.value = {
      milesCardNumber,
      hasMilesCard: !!milesCardNumber,
    };
    // Сохраняем исходное значение при синхронизации
    initialPassengerOnCard.value = props.passengerOnCard
      ? clone(props.passengerOnCard)
      : null;

    if (props.isNewPassenger) {
      validations.value.$reset();
    }
  };
  const onClose = () => {
    const hasUnsavedData = !isEqual(
      selectedPassenger.value,
      draftPassenger.value,
    );
    if (hasUnsavedData) {
      modal.show(unsavedModalName, {
        title: t('passengersData.multiPassengers.unsavedData.title'),
        message: t('passengersData.multiPassengers.unsavedData.description'),
      });
    } else {
      draftPassenger.value = props.passengerOnCard;
      bottomSheet.hide(props.name);
    }
  };

  watch(
    () => props.passengerOnCard,
    newVal => {
      // Обновляем draftPassenger только если форма не открыта,
      // чтобы не перезаписывать изменения пользователя во время редактирования
      // initialPassengerOnCard обновляется только при открытии формы через syncDraftPassenger
      if (!bottomSheet.isVisible(props.name)) {
        draftPassenger.value = newVal;
      }
    },
    { deep: true },
  );

  const onBottomSheetCloseAgree = () => {
    draftPassenger.value = props.passengerOnCard;
    isSubmitAttempted.value = false;
    bottomSheet.hide(props.name);
  };

  const isMilesCardValid = () => {
    return (
      !milesCardState.value.hasMilesCard ||
      !!milesCardState.value.milesCardNumber.trim()
    );
  };

  function saveLocalPassenger(newPassenger: Passenger) {
    try {
      const stored = sessionStorage.getItem(
        savedPassengersConfig.SESSION_STORAGE_KEY,
      );
      const passengers = stored ? JSON.parse(stored) : [];
      // Если пассажир уже существует (по id или номеру документа), обновляем его
      const existingIdx = passengers.findIndex(
        (pax: Passenger) =>
          pax.id === newPassenger.id ||
          pax.document.number === newPassenger.document.number,
      );
      if (existingIdx >= 0) {
        passengers[existingIdx] = newPassenger;
      } else {
        passengers.push(newPassenger);
      }
      sessionStorage.setItem(
        savedPassengersConfig.SESSION_STORAGE_KEY,
        JSON.stringify(passengers),
      );
    } catch (e) {
      sentryModel.captureException(
        sentryModel.getReadableError('ERR_FAILED_TO_SAVE_LOCAL_PASSENGER', e),
      );
    }
  }

  const createPassengerMutation = formPassengerModel.useCreatePassenger();

  const { mutate: editPassengerToAccount } =
    formPassengerModel.useEditPassenger();

  const userStore = useUserStore();

  const isNewPaxUnique = (newPax: Passenger) => {
    if (!props.isNewPassenger && props.passengerOnCard) {
      return true;
    }

    // Для новых пассажиров или при изменении номера документа проверяем уникальность
    return !props.allPassengers.some((localPax, idx) => {
      return (
        idx !== props.passengerIdx &&
        localPax.document.number === newPax.document.number
      );
    });
  };

  const onSubmit = async () => {
    isSubmitAttempted.value = true;
    validations.value.$touch();

    let hasError = validations.value.$error;

    if (!isMilesCardValid()) {
      hasError = true;
    }

    if (!hasError && draftPassenger.value) {
      if (isNewPaxUnique(draftPassenger.value)) {
        const userId = await userStore.getUserId();
        const mappedPassenger = convertPassengerToPayload({
          passenger: draftPassenger.value,
        });

        if (props.isNewPassenger) {
          const draftPassengerWithLocalId = {
            ...draftPassenger.value,
            id: uuid(),
          };
          // Для нового пассажира сначала сохраняем карту на временный id.
          milesCardsStorageLib.upsertMilesCardByPassengerId(
            draftPassengerWithLocalId.id,
            String(milesCardState.value.milesCardNumber),
          );
          saveLocalPassenger(draftPassengerWithLocalId);
          emit('onPassengerCreate', draftPassengerWithLocalId);
          if (userId) {
            createPassengerMutation
              .createPassengerWithCallback(
                { userId, passenger: mappedPassenger },
                createdPassenger => {
                  const passengerWithBackendId = {
                    ...draftPassengerWithLocalId,
                    id: createdPassenger.id,
                  };
                  // После ответа API переносим карту на постоянный id.
                  milesCardsStorageLib.rebindMilesCardPassengerId(
                    draftPassengerWithLocalId.id,
                    createdPassenger.id,
                  );
                  saveLocalPassenger(passengerWithBackendId);
                  emit('onPassengerCreate', passengerWithBackendId);
                },
              )
              .catch(() => {
                // Ошибка уже обработана в мутации / API
              });
          }
        } else {
          if (draftPassenger.value.id) {
            // Для существующего пассажира обновляем карту сразу по его id.
            if (!milesCardState.value.hasMilesCard) {
              milesCardsStorageLib.removeMilesCardByPassengerId(
                draftPassenger.value.id,
              );
            } else {
              milesCardsStorageLib.upsertMilesCardByPassengerId(
                draftPassenger.value.id,
                String(milesCardState.value.milesCardNumber),
              );
            }
          }
          emit('onPassengerEdit', draftPassenger.value, props.passengerIdx);
          // Сравниваем с исходным значением, а не с текущим passengerOnCard,
          // так как passengerOnCard может обновляться через watch
          if (
            userId &&
            initialPassengerOnCard.value &&
            !isEqual(draftPassenger.value, initialPassengerOnCard.value)
          ) {
            editPassengerToAccount({ userId, passenger: mappedPassenger });
          }
        }
        isSubmitAttempted.value = false;
        bottomSheet.hide(props.name);
      } else {
        hasError = true;
        handleDuplicatePaxError();
      }
    }
    analyticsEventsLib.savedPaxSaveClick({
      has_error: hasError,
      firstName: draftPassenger.value?.firstName,
      lastName: draftPassenger.value?.lastName,
      providerClass: props.providerClass,
    });
  };
</script>
